import type { ComponentType } from "react";
import type { IconProps } from "../../shared/Icon";
import {
  Cpu,
  FilePlus,
  FileText,
  Folder,
  Globe,
  ListChecks,
  Pencil,
  Search,
  Sparkles,
  Terminal,
} from "../../shared/Icon";
import type { ToolStatus } from "../../shared/StatusBadge";

/**
 * One place that knows how to read a tool call: which family it belongs to,
 * what icon + verb to show, and how to pull the one thing worth putting in the
 * header (a path, a command, a query). Keeps the cards dumb and consistent.
 */
export type ToolCategory =
  | "read"
  | "write"
  | "shell"
  | "search"
  | "fetch"
  | "task"
  | "todo"
  | "other";

export interface ToolMeta {
  category: ToolCategory;
  /** Short action verb shown in the card header. */
  label: string;
  Icon: ComponentType<IconProps>;
  /** Write/Edit-family calls render as artifact cards. */
  isArtifact: boolean;
}

const TABLE: Record<string, ToolMeta> = {
  bash: { category: "shell", label: "Shell", Icon: Terminal, isArtifact: false },
  shell: { category: "shell", label: "Shell", Icon: Terminal, isArtifact: false },
  read: { category: "read", label: "Read", Icon: FileText, isArtifact: false },
  ls: { category: "read", label: "List", Icon: Folder, isArtifact: false },
  write: { category: "write", label: "Write", Icon: FilePlus, isArtifact: true },
  edit: { category: "write", label: "Edit", Icon: Pencil, isArtifact: true },
  multiedit: { category: "write", label: "Edit", Icon: Pencil, isArtifact: true },
  notebookedit: { category: "write", label: "Edit", Icon: Pencil, isArtifact: true },
  grep: { category: "search", label: "Search", Icon: Search, isArtifact: false },
  glob: { category: "search", label: "Find", Icon: Search, isArtifact: false },
  search: { category: "fetch", label: "Web search", Icon: Globe, isArtifact: false },
  websearch: { category: "fetch", label: "Web search", Icon: Globe, isArtifact: false },
  webfetch: { category: "fetch", label: "Fetch", Icon: Globe, isArtifact: false },
  task: { category: "task", label: "Agent", Icon: Sparkles, isArtifact: false },
  todowrite: { category: "todo", label: "Plan", Icon: ListChecks, isArtifact: false },
};

const FALLBACK: ToolMeta = {
  category: "other",
  label: "Tool",
  Icon: Cpu,
  isArtifact: false,
};

export function toolMeta(name: string): ToolMeta {
  const m = TABLE[name.toLowerCase()];
  if (m) return m;
  // Unknown tool: keep its own name as the verb so nothing is hidden.
  return { ...FALLBACK, label: name || "Tool" };
}

/** The one salient field for the header — path, command, query, etc. */
export function toolTarget(
  name: string,
  input: Record<string, unknown> | undefined,
): string {
  if (!input || typeof input !== "object") return "";
  const lower = name.toLowerCase();
  if (lower === "bash" || lower === "shell") return firstLine(String(input.command ?? ""));
  if (lower === "read" || lower === "ls")
    return String(input.file_path ?? input.path ?? "");
  if (lower === "glob") return String(input.pattern ?? input.path ?? "");
  if (lower === "grep") {
    const pattern = String(input.pattern ?? "");
    const path = String(input.path ?? input.glob ?? "");
    return path ? `${pattern}  in  ${path}` : pattern;
  }
  if (
    lower === "edit" ||
    lower === "multiedit" ||
    lower === "write" ||
    lower === "notebookedit"
  )
    return String(input.file_path ?? input.path ?? "");
  if (lower === "todowrite") {
    const todos = (input.todos as unknown[]) ?? [];
    return `${todos.length} item${todos.length === 1 ? "" : "s"}`;
  }
  if (lower === "task") return String(input.description ?? input.prompt ?? "").slice(0, 200);
  if (lower === "webfetch" || lower === "websearch" || lower === "search")
    return String(input.url ?? input.query ?? "");
  for (const [k, v] of Object.entries(input)) {
    if (typeof v === "string" && v.length < 200) return `${k}: ${v}`;
  }
  return Object.keys(input).join(", ");
}

/** File path for artifact-producing calls, else null. */
export function toolFilePath(
  input: Record<string, unknown> | undefined,
): string | null {
  if (!input) return null;
  const p = input.file_path ?? input.path;
  return typeof p === "string" && p ? p : null;
}

/** Content a Write call produced, for an optional artifact preview. */
export function toolWriteContent(
  input: Record<string, unknown> | undefined,
): string | null {
  if (!input) return null;
  const c = input.content ?? input.new_string;
  return typeof c === "string" && c ? c : null;
}

export function artifactVerb(name: string): string {
  return name.toLowerCase() === "write" ? "created" : "updated";
}

export function fileName(path: string): string {
  const clean = path.replace(/\/+$/, "");
  const i = clean.lastIndexOf("/");
  return i === -1 ? clean : clean.slice(i + 1);
}

/** Status of a tool call given whether its result arrived and errored. */
export function deriveToolStatus(
  hasResult: boolean,
  isError: boolean,
): ToolStatus {
  if (!hasResult) return "running";
  return isError ? "error" : "success";
}

function firstLine(s: string): string {
  const i = s.indexOf("\n");
  return i === -1 ? s : `${s.slice(0, i)} …`;
}
